import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertTriangle } from 'lucide-react';
import ParticleField from '../components/ThreeScene/ParticleField';
import { useMousePosition } from '../hooks/useMousePosition';
import { fadeInUp, staggerContainer } from '../utils/animations';

const TermsOfService = () => {
  const mousePosition = useMousePosition();

  return (
    <div className="relative min-h-screen bg-dark-surface">
      {/* 3D Canvas Background */}
      <div className="absolute inset-0 z-0">
        <Canvas
          camera={{ position: [0, 0, 15], fov: 60 }}
          gl={{ antialias: true, alpha: true }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.3} />
            <pointLight position={[10, 10, 10]} intensity={0.8} />
            <ParticleField
              count={1500}
              radius={12}
              colors={['#6C5CE7', '#1F51FF']}
              mousePosition={mousePosition}
            />
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              autoRotate
              autoRotateSpeed={0.2}
              maxPolarAngle={Math.PI / 2}
              minPolarAngle={Math.PI / 2}
            />
          </Suspense>
        </Canvas>
      </div>
      {/* Hero Section */}
      <section className="relative z-10 py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-dark-surface/80 to-dark-surface-100/80">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <FileText size={64} className="text-neon-blue mx-auto mb-6" />
            <h1 className="text-h1 font-bold mb-4">
              <span className="gradient-text">Terms of Service</span>
            </h1>
            <p className="text-body-lg text-text-secondary max-w-2xl mx-auto">
              Please read these terms carefully before using our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="space-y-12"
          >
            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="text-neon-purple" size={24} />
                <h2 className="text-h3 font-bold">Acceptance of Terms</h2>
              </div>
              <p className="text-text-secondary">
                By accessing and using our services, you accept and agree to be bound by the 
                terms and provision of this agreement. If you do not agree to abide by the 
                above, please do not use this service.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Use License</h2>
              <p className="text-text-secondary mb-4">
                Permission is granted to temporarily download one copy of the materials on 
                STARZ Tech's website for personal, non-commercial transitory viewing only.
              </p>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Modify or copy the materials</li>
                <li>Use the materials for commercial purposes</li>
                <li>Attempt to reverse engineer any software</li>
                <li>Remove any copyright or proprietary notations</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="text-neon-blue" size={24} />
                <h2 className="text-h3 font-bold">Disclaimer</h2>
              </div>
              <p className="text-text-secondary">
                The materials on STARZ Tech's website are provided on an 'as is' basis. 
                STARZ Tech makes no warranties, expressed or implied, and hereby disclaims 
                and negates all other warranties including without limitation, implied 
                warranties or conditions of merchantability.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Limitations</h2>
              <p className="text-text-secondary">
                In no event shall STARZ Tech or its suppliers be liable for any damages 
                (including, without limitation, damages for loss of data or profit, or 
                due to business interruption) arising out of the use or inability to use 
                the materials on STARZ Tech's website.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Contact Information</h2>
              <p className="text-text-secondary">
                If you have any questions about these Terms of Service, please contact us at 
                <span className="text-neon-blue"> legal@starztech.com</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-neon-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-neon-purple/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};

export default TermsOfService;